import { ReactNode, useEffect } from 'react'
import cn from 'classnames'
import { BogoBanner } from '@mc/bogo'
import {
  CHECKOUT_I18N_NAMESPACE,
  IntroPricingBanner,
  Navigation,
  PlansPrimerCertsValue,
} from '@mc/checkout'
import { Button, IconCheckDefault as Check } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { paths } from '@mc/paths'
import { PromoBanner } from '@mc/promos'
import { Locations, PageSource, track } from '@mc/track-event'

const PRIMER_SCREEN_VIEWED_EVENT = 'Primer Screen Viewed'
const PRIMER_SCREEN_CTA_CLICKED_EVENT = 'Primer Screen CTA Clicked'

interface PlansPrimerPageProps {
  dismiss: () => void
}

const PlansPrimerPageBody = ({ dismiss }: PlansPrimerPageProps) => (
  <section className='container d-flex justify-content-center align-items-center flex-column mc-pb-9 mc-px-4'>
    <PlansPrimerTitle />
    <div className='row mc-w-100 justify-content-center'>
      <div className='d-flex justify-content-center align-items-center flex-column'>
        <ValuesSection />
        <PlansPrimerCertsValue />
        <div className='mc-mt-9' />
        <CtaSection setIsDismissed={dismiss} />
      </div>
    </div>
  </section>
)

export const PlansPrimerPage = ({ dismiss }: PlansPrimerPageProps) => {
  useEffect(() => {
    track(PRIMER_SCREEN_VIEWED_EVENT, {
      location: Locations.PLANS_PRIMER_PAGE,
      page_source: PageSource.PLANS,
    })
  }, [])

  return (
    <div
      className='d-flex flex-column'
      style={{
        minHeight: '100dvh',
      }}
    >
      <Navigation />
      <PromoBanner pathname={paths.checkout} />
      <BogoBanner location='checkout' />
      <IntroPricingBanner />
      <PlansPrimerPageBody dismiss={dismiss} />
    </div>
  )
}

export const PlansPrimerTitle = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  return (
    <h3 className='mc-text-h3 mc-mt-9 mc-mb-8 mc-text--center'>
      {t('plansPrimer.title')}
    </h3>
  )
}

export const ValuesSection = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  const instructors = getNumberOfInstructors()

  const values: Array<{ key: string; content: ReactNode; bold?: boolean }> = [
    {
      key: 'satisfaction',
      content: t('plansPrimer.values.satisfaction'),
      bold: true,
    },
    {
      key: 'allClasses',
      content: t('plansPrimer.values.allClasses', { instructors }),
    },
    { key: 'devices', content: t('plansPrimer.values.devices') },
    { key: 'newClasses', content: t('plansPrimer.values.newClasses') },
    { key: 'audioOnly', content: t('plansPrimer.values.audioOnly') },
  ]

  return (
    <ul className='mc-overflow--hidden d-flex flex-column'>
      {values.map(({ key, content, bold = false }) => (
        <li key={key} className='d-flex mc-mt-2'>
          <div>
            <Check
              style={{ height: 'var(--mc-scale-4)' }}
              className='mc-opacity--hinted mc-mr-3'
            />
          </div>
          <p className={cn({ 'mc-text--bold': bold })}>{content}</p>
        </li>
      ))}
    </ul>
  )
}

const CtaSection = ({
  setIsDismissed,
}: {
  setIsDismissed: (isDismissed: boolean) => void
}) => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  const onCtaClicked = () => {
    setIsDismissed(true)

    track(PRIMER_SCREEN_CTA_CLICKED_EVENT, {
      location: Locations.PLANS_PRIMER_PAGE,
      page_source: PageSource.PLANS,
      action: t('plansPrimer.cta'),
    })
  }

  return (
    <Button fullWidth onClick={onCtaClicked}>
      {t('plansPrimer.cta')}
    </Button>
  )
}
